import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * SlideOutDrawer - A slide-in panel from the right side
 * Used to display detailed node attributes without blocking the main view
 *
 * @param {boolean} isOpen - Controls drawer visibility
 * @param {function} onClose - Callback when drawer is closed
 * @param {string} title - Drawer header title
 * @param {ReactNode} children - Drawer content
 * @param {string} width - Drawer width (default: '400px')
 * @param {boolean} resizable - Show a drag handle on the left edge (default: false)
 * @param {function} onResizeDelta - Called with the horizontal drag delta in px
 *   (positive = wider, since the drawer is anchored to the right edge). The
 *   caller owns clamping/persisting the resulting width.
 * @param {boolean} modal - Dim the background, block clicks through to it, and
 *   lock page scroll while open (default: true). Set false for a non-modal
 *   "inspector" style panel that stays open while the user keeps interacting
 *   with the page behind it (e.g. selecting different rows in a table).
 */
export default function SlideOutDrawer({
  isOpen, onClose, title, children, width = '400px', resizable = false, onResizeDelta, modal = true,
}) {
  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open (modal mode only — a non-modal
  // panel should let the page behind it keep scrolling/interacting normally)
  useEffect(() => {
    if (isOpen && modal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, modal]);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    let startX = e.clientX;

    const handleMouseMove = (moveEvent) => {
      // Anchored to the right edge — dragging the left-edge handle further
      // left (clientX decreasing) should grow the drawer.
      const delta = startX - moveEvent.clientX;
      if (delta !== 0) {
        onResizeDelta?.(delta);
        startX = moveEvent.clientX;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <>
      {/* Overlay (modal mode only) */}
      {isOpen && modal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onClose}
          style={{ opacity: isOpen ? 1 : 0 }}
        />
      )}

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full bg-background-secondary border-l border-border-primary z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out"
        style={{
          width,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {resizable && isOpen && (
          <div
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
            className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-accent-primary-base/50 active:bg-accent-primary-base/70"
            style={{ transform: 'translateX(-50%)' }}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-primary flex-shrink-0">
          <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
            aria-label="Close drawer"
          >
            <X size={20} className="text-text-secondary hover:text-text-primary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </>
  );
}
